import { useCallback, useEffect, useRef, useState } from 'react';
import {
  installManifestHomeSplitSync,
  removeManifestBubbleWidget,
} from '../lib/manifestAvatarBridge';
import { installAvatarHomeNavigationGuard } from '../lib/avatarHomeNavigationGuard';
import {
  AVATAR_SESSION_LIMIT_MINUTES,
  AVATAR_SESSION_LIMIT_MS,
  requestManifestAvatarHangup,
} from '../lib/avatarEmbed';



export const MANIFEST_PORTAL = 'https://concierge.rolemodel.ai';

export const MANIFEST_AVATAR_SLUG = 'milan-self-labs';

/** Empty so the embed does not render a title behind the SELF Labs logo. */
export const MANIFEST_AVATAR_TITLE = '';

export const MANIFEST_CONTAINER_ID = 'manifest-avatar-container';



const SCRIPT_SRC = `${MANIFEST_PORTAL}/static/widget/launcher.js`;

const SCRIPT_ID = 'manifest-avatar-launcher';

const HOST_TOOLS_ATTR = 'data-rolemodel-host-tools';

const HOST_TOOLS_SCRIPT_ID = 'manifest-host-tools-script';

const HOST_TOOLS_CSS_ID = 'manifest-host-tools-css';



export const AVATAR_CONVERSATION_START_EVENT = 'selflabs:start-avatar-conversation';
export const AVATAR_CONVERSATION_END_EVENT = 'selflabs:end-avatar-conversation';
export const AVATAR_CONVERSATION_STATE_EVENT = 'selflabs:avatar-conversation-state';

export type AvatarConversationUiState = {
  conversationStarted: boolean;
  status: 'idle' | 'loading' | 'ready' | 'error' | 'ended';
  sessionEnded: boolean;
};

export function startAvatarConversation(): void {
  window.dispatchEvent(new CustomEvent(AVATAR_CONVERSATION_START_EVENT));
}

export function endAvatarConversation(): void {
  window.dispatchEvent(new CustomEvent(AVATAR_CONVERSATION_END_EVENT));
}

function publishAvatarConversationState(state: AvatarConversationUiState): void {
  window.dispatchEvent(
    new CustomEvent(AVATAR_CONVERSATION_STATE_EVENT, { detail: state })
  );
}

export function buildEmbedUrl(autostart = false): string {

  const qs = new URLSearchParams({

    slug: MANIFEST_AVATAR_SLUG,

    layout: 'fullscreen',

    page_tools: '1',

    image_gen: '1',

    video_gen: '1',

    host_chrome: '1',

    autostart: autostart ? '1' : '0',

  });

  return `${MANIFEST_PORTAL}/widget/embed?${qs}`;

}

type RolemodelPageTarget = {
  target: string;
  id?: string;
  label: string;
  tag: string;
  y: number;
};

const MAX_PAGE_TARGETS_BYTES = 3500;

function compactRolemodelTargets(targets: RolemodelPageTarget[]): RolemodelPageTarget[] {
  const scanned = targets.map((t) => ({
    target: String(t.target || '').slice(0, 64),
    id: t.id ? String(t.id).slice(0, 64) : undefined,
    label: String(t.label || t.target || '')
      .replace(/\u2122/g, '')
      .replace(/[\u2013\u2014]/g, '-')
      .slice(0, 48),
    tag: String(t.tag || 'div'),
    y: typeof t.y === 'number' ? t.y : 0,
  }));
  const out: RolemodelPageTarget[] = [];
  let size = '{"pageTargets":[]}'.length;
  for (const t of scanned) {
    const next = size + JSON.stringify(t).length + (out.length ? 1 : 0);
    if (next > MAX_PAGE_TARGETS_BYTES) break;
    out.push(t);
    size = next;
  }
  return out;
}

/** Prefer short data-avatar-target anchors so Concierge connect stays under size limits. */
function scanSelfLabsPageTargets(): RolemodelPageTarget[] {
  const targets: RolemodelPageTarget[] = [];
  const seen: Record<string, true> = {};
  document.querySelectorAll('[data-avatar-target]').forEach((el) => {
    const target = el.getAttribute('data-avatar-target') || '';
    if (!target || seen[target]) return;
    seen[target] = true;
    const rect = el.getBoundingClientRect();
    targets.push({
      target,
      id: el.id || undefined,
      label:
        el.getAttribute('aria-label') ||
        target,
      tag: el.tagName.toLowerCase(),
      y: Math.round(rect.top + window.scrollY),
    });
  });
  return targets;
}

function installRolemodelCompactTargets(): void {
  const win = window as Window & {
    __rolemodelGetPageTargets?: () => RolemodelPageTarget[];
    __selflabsRolemodelTargetsInstalled?: boolean;
  };
  if (win.__selflabsRolemodelTargetsInstalled) return;
  win.__selflabsRolemodelTargetsInstalled = true;

  win.__rolemodelGetPageTargets = () => compactRolemodelTargets(scanSelfLabsPageTargets());
}



/** Load Concierge host-tools on the parent page (homepage iframe does not use launcher.js). */

export function ensureManifestHostTools(): Promise<void> {
  const finish = () => {
    installRolemodelCompactTargets();
  };

  const rolemodelReady = () =>
    typeof (window as Window & { __rolemodelPageAction?: unknown }).__rolemodelPageAction ===
      'function' ||
    typeof (window as Window & { __rolemodelGetPageTargets?: unknown }).__rolemodelGetPageTargets ===
      'function';

  if (document.documentElement.getAttribute(HOST_TOOLS_ATTR) === '1') {
    if (rolemodelReady()) {
      finish();
      return Promise.resolve();
    }

    const existing = document.getElementById(HOST_TOOLS_SCRIPT_ID);
    if (existing) {
      return new Promise((resolve, reject) => {
        existing.addEventListener(
          'load',
          () => {
            finish();
            resolve();
          },
          { once: true }
        );
        existing.addEventListener(
          'error',
          () => reject(new Error('Role Model host-tools failed to load')),
          { once: true }
        );
      });
    }
  }

  document.documentElement.setAttribute(HOST_TOOLS_ATTR, '1');

  if (!document.getElementById(HOST_TOOLS_CSS_ID)) {
    const link = document.createElement('link');
    link.id = HOST_TOOLS_CSS_ID;
    link.rel = 'stylesheet';
    link.href = `${MANIFEST_PORTAL}/static/widget/host-tools.css`;
    document.head.appendChild(link);
  }

  const existingScript = document.getElementById(HOST_TOOLS_SCRIPT_ID);
  if (existingScript) {
    if (rolemodelReady()) {
      finish();
      return Promise.resolve();
    }
    return new Promise((resolve, reject) => {
      existingScript.addEventListener(
        'load',
        () => {
          finish();
          resolve();
        },
        { once: true }
      );
      existingScript.addEventListener(
        'error',
        () => reject(new Error('Role Model host-tools failed to load')),
        { once: true }
      );
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.id = HOST_TOOLS_SCRIPT_ID;
    script.src = `${MANIFEST_PORTAL}/static/widget/host-tools.js`;
    script.async = true;
    script.setAttribute('data-rolemodel-host-tools', '1');
    script.addEventListener(
      'load',
      () => {
        finish();
        resolve();
      },
      { once: true }
    );
    script.addEventListener(
      'error',
      () => reject(new Error('Role Model host-tools failed to load')),
      { once: true }
    );
    document.head.appendChild(script);
  });
}



function appendManifestScript(attrs: Record<string, string>): Promise<void> {

  return new Promise((resolve, reject) => {

    const existing = document.getElementById(SCRIPT_ID);

    if (existing) {

      existing.remove();

      document.querySelectorAll(`[data-manifest-avatar]`).forEach((el) => el.remove());

    }



    const script = document.createElement('script');

    script.id = SCRIPT_ID;

    script.src = SCRIPT_SRC;

    script.async = true;

    Object.entries(attrs).forEach(([key, value]) => {

      script.setAttribute(key, value);

    });



    script.addEventListener('load', () => resolve());

    script.addEventListener('error', () => reject(new Error('Manifest avatar script failed to load')));

    document.body.appendChild(script);

  });

}



/** Homepage: direct fullscreen-layout iframe filling the viewport */
export function ManifestAvatarHomeEmbed() {
  const [conversationStarted, setConversationStarted] = useState(false);
  const [status, setStatus] = useState<'idle' | 'loading' | 'ready' | 'error' | 'ended'>('idle');
  const [sessionEnded, setSessionEnded] = useState(false);
  const conversationStartedRef = useRef(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const sessionTimerRef = useRef<number | null>(null);

  const clearSessionTimer = useCallback(() => {
    if (sessionTimerRef.current != null) {
      window.clearTimeout(sessionTimerRef.current);
      sessionTimerRef.current = null;
    }
  }, []);

  const publishState = useCallback(
    (
      next: Partial<AvatarConversationUiState> &
        Pick<AvatarConversationUiState, 'conversationStarted' | 'status' | 'sessionEnded'>
    ) => {
      publishAvatarConversationState(next);
    },
    []
  );

  const endSession = useCallback(
    (reason: 'timeout' | 'user') => {
      clearSessionTimer();
      requestManifestAvatarHangup(iframeRef.current);
      conversationStartedRef.current = false;
      setConversationStarted(false);
      setSessionEnded(reason === 'timeout' || reason === 'user');
      setStatus('ended');
      publishState({
        conversationStarted: false,
        status: 'ended',
        sessionEnded: true,
      });
    },
    [clearSessionTimer, publishState]
  );

  const beginConversation = useCallback(() => {
    if (conversationStartedRef.current) return;
    setSessionEnded(false);
    conversationStartedRef.current = true;
    setConversationStarted(true);
    setStatus('loading');
    publishState({
      conversationStarted: true,
      status: 'loading',
      sessionEnded: false,
    });
  }, [publishState]);

  // Host-tools must load on mount so Milan page actions work before/during conversation.
  useEffect(() => {
    let cancelled = false;
    removeManifestBubbleWidget();
    installAvatarHomeNavigationGuard();
    ensureManifestHostTools()
      .then(() => {
        if (!cancelled) installManifestHomeSplitSync();
      })
      .catch(() => {
        /* host-tools optional for chips; avatar tools need it when conversation starts */
      });
    publishState({
      conversationStarted: false,
      status: 'idle',
      sessionEnded: false,
    });
    return () => {
      cancelled = true;
    };
  }, [publishState]);

  useEffect(() => {
    const onStart = () => beginConversation();
    const onEnd = () => {
      if (conversationStartedRef.current) endSession('user');
    };
    window.addEventListener(AVATAR_CONVERSATION_START_EVENT, onStart);
    window.addEventListener(AVATAR_CONVERSATION_END_EVENT, onEnd);
    return () => {
      window.removeEventListener(AVATAR_CONVERSATION_START_EVENT, onStart);
      window.removeEventListener(AVATAR_CONVERSATION_END_EVENT, onEnd);
    };
  }, [beginConversation, endSession]);

  // Concierge embed posts session-state; do not treat iframe onLoad as "live".
  useEffect(() => {
    if (!conversationStarted) return;

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== MANIFEST_PORTAL) return;
      const data = event.data;
      if (!data || typeof data !== 'object') return;
      const record = data as Record<string, unknown>;
      if (record.source !== 'rolemodel-embed' || record.type !== 'session-state') return;

      const embedStatus = typeof record.status === 'string' ? record.status : '';
      if (embedStatus === 'live') {
        setStatus('ready');
        publishState({
          conversationStarted: true,
          status: 'ready',
          sessionEnded: false,
        });
        return;
      }
      if (embedStatus === 'connecting') {
        setStatus('loading');
        publishState({
          conversationStarted: true,
          status: 'loading',
          sessionEnded: false,
        });
        return;
      }
      if (embedStatus === 'error') {
        setStatus('error');
        publishState({
          conversationStarted: true,
          status: 'error',
          sessionEnded: false,
        });
      }
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [conversationStarted, publishState]);

  useEffect(() => {
    if (status !== 'ready') return;

    sessionTimerRef.current = window.setTimeout(() => {
      endSession('timeout');
    }, AVATAR_SESSION_LIMIT_MS);

    return clearSessionTimer;
  }, [status, endSession, clearSessionTimer]);

  useEffect(() => () => clearSessionTimer(), [clearSessionTimer]);

  const showStartOverlay =
    !conversationStarted || status === 'loading' || status === 'ended';

  return (
    <div
      id={MANIFEST_CONTAINER_ID}
      className="manifest-avatar-host absolute inset-0 w-full h-full"
    >
      {showStartOverlay && (
        <div className="avatar-start-overlay absolute inset-0 z-20 flex flex-col items-center justify-center">
          <div className="avatar-start-overlay__inner">
            {sessionEnded ? (
              <>
                <p className="avatar-start-overlay__title">
                  Your {AVATAR_SESSION_LIMIT_MINUTES}-minute session has ended.
                </p>
                <p className="avatar-start-overlay__subtitle">
                  You can still explore SELF Labs in the panel on the right, or start a new
                  conversation anytime with the Talk button below.
                </p>
              </>
            ) : conversationStarted && status === 'loading' ? (
              <>
                <p className="avatar-start-overlay__title">Connecting to ambassador</p>
                <p className="avatar-start-overlay__hint">
                  (You may need to enable your microphone in your browser for the session to
                  start — look out for a notification to allow access.)
                </p>
                <div className="avatar-start-overlay__spinner" aria-label="Connecting to ambassador" />
              </>
            ) : (
              <>
                <p className="avatar-start-overlay__title">
                  Start a Conversation with our ambassador to learn more about SELF Labs Inc
                </p>
                <p className="avatar-start-overlay__subtitle">
                  Sessions are limited to {AVATAR_SESSION_LIMIT_MINUTES} minutes. Use Talk next to
                  Browse site to begin.
                </p>
              </>
            )}
          </div>
        </div>
      )}

      {status === 'error' && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-[#0b0f14] p-6 text-center">
          <p className="text-gray-400 text-sm max-w-md">
            The interactive avatar is temporarily unavailable, but you can still explore SELF Labs
            in the panel on the right.
          </p>
        </div>
      )}

      {conversationStarted && status !== 'error' && status !== 'ended' && (
        <iframe
          ref={iframeRef}
          data-manifest-avatar="true"
          src={buildEmbedUrl(true)}
          title="Milan (SELF Labs)"
          allow="microphone; camera; autoplay"
          className="avatar-embed-frame__iframe absolute inset-0 w-full h-full border-0 bg-[#0b0f14]"
        />
      )}
    </div>
  );
}



/** Non-home routes: corner bubble widget */

function ManifestAvatarWidget() {

  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');



  useEffect(() => {

    let cancelled = false;



    appendManifestScript({

      'data-portal': MANIFEST_PORTAL,

      'data-avatar-slug': MANIFEST_AVATAR_SLUG,

      'data-title': MANIFEST_AVATAR_TITLE || 'Milan',

      'data-mode': 'bubble',

      'data-position': 'right',

      'data-accent-color': '#6c5ce7',

      'data-label': 'Need help?',

      'data-page-tools': '1',

      'data-image-gen': '1',

      'data-video-gen': '1',

    })

      .then(() => {

        if (!cancelled) setStatus('ready');

      })

      .catch(() => {

        if (!cancelled) setStatus('error');

      });



    return () => {

      cancelled = true;

    };

  }, []);



  if (status === 'error') {

    return (

      <div

        className="fixed bottom-24 right-6 z-40 max-w-xs p-3 rounded-lg bg-[#0d0b14]/90 border border-purple-500/20 text-xs text-gray-400 hidden md:block"

        role="status"

      >

        The interactive avatar is temporarily unavailable.

      </div>

    );

  }



  return null;

}



export default function ManifestAvatarEmbed() {
  useEffect(() => {
    removeManifestBubbleWidget();
    const id = window.setInterval(removeManifestBubbleWidget, 2000);
    return () => window.clearInterval(id);
  }, []);

  // Milan lives on the homepage embed only — no corner "Need help?" bubble.
  return null;
}



export function openManifestAvatar(): void {

  const hero = document.getElementById('avatar-hero');

  if (hero) {

    hero.scrollIntoView({ behavior: 'smooth', block: 'start' });

    return;

  }

  const btn = document.querySelector<HTMLButtonElement>(

    'button[aria-label*="Milan"], button[aria-label*="Open"]'

  );

  btn?.click();

}



declare global {

  interface Window {

    __manifestRunAvatarTool?: (tool: string, args?: Record<string, unknown>) => unknown;

  }

}


